/**
 * @file CompanyInvoicesPage.tsx
 * @brief Company Admin → invoices dashboard (super-admin parity, company scope).
 *
 * @author DES
 * @date 19-06-2026
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Download,
  ExternalLink,
  FileArchive,
  LoaderCircle,
  PoundSterling,
  Receipt,
} from "lucide-react";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { searchGlass } from "@/components/ui/ExpandingSearch";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { MoneyAmount, MoneyBadges } from "@/shared/billing/MoneyCells";
import type { MoneyRowLike } from "@/shared/billing/moneyStatus";
import { Button } from "@/components/ui/Button";
import { StatCard } from "@/features/analytics/components/StatCard";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { ApiClientError, apiClient } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  formatDateTime,
  formatFullName,
  formatMoneyMinor,
  getInitials,
} from "@/shared/utils/formatters";
import {
  companyInvoicesExportUrl,
  invoiceOwnerGroupKey,
  useCompanyInvoicesEmployees,
  useCompanyInvoicesGroupRows,
  useCompanyInvoicesList,
} from "./api";
import {
  companyInvoicesPagination,
  invoiceCreatedAt,
  invoiceEmployeeLabel,
  resolveInvoiceTotals,
  isMixedCurrency,
  type CompanyInvoiceRow,
  type CompanyInvoicesListParams,
  type InvoicePeriod,
} from "./schemas";
import { InvoiceMenu } from "@/components/feedback/InvoiceMenu";
import {
  CompanyInvoicesFilters,
  CompanyInvoicesHeaderFilters,
} from "./components/CompanyInvoicesFilters";

function paramsFromSearch(search: URLSearchParams): CompanyInvoicesListParams {
  const period = search.get("period");
  return {
    page: Number(search.get("page")) || 1,
    pageSize: Number(search.get("pageSize")) || 25,
    q: search.get("q") || undefined,
    from: search.get("from") || undefined,
    to: search.get("to") || undefined,
    employee_id: search.get("employee_id") || undefined,
    period:
      period === "mtd" || period === "3m" || period === "all"
        ? period
        : "all",
  };
}

function searchFromParams(p: CompanyInvoicesListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.pageSize && p.pageSize !== 25) usp.set("pageSize", String(p.pageSize));
  if (p.q) usp.set("q", p.q);
  if (p.from) usp.set("from", p.from);
  if (p.to) usp.set("to", p.to);
  if (p.employee_id) usp.set("employee_id", p.employee_id);
  if (p.period && p.period !== "all") usp.set("period", p.period);
  return usp;
}

export function CompanyInvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);
  // Row click opens that row's invoice menu (anchored to its button).
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const isGrouped = !params.employee_id;
  /**
   * Grouped (no employee picked): every employee with an invoice, from the
   * server, not the employees who happen to be on the current page (DES
   * 01-10-2026: "is page per bhi whoi, pagination ki waja say masla hai users
   * group wala"), as on Sessions. A group fetches its own invoices when
   * opened, 25 at a time. The list call stays for the two total cards (its
   * totals cover every matching row), asking for a single row.
   */
  const list = useCompanyInvoicesList(isGrouped ? { ...params, page: 1, pageSize: 1 } : params);
  const employees = useCompanyInvoicesEmployees(params, isGrouped);
  // Open groups live in the URL (`open=key:batches,…`), as on Sessions.
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.q?.trim());
  const openList = useMemo(() => {
    const keys = searching
      ? (employees.data ?? []).map((e) => invoiceOwnerGroupKey(e.owner_id))
      : [...openGroups.keys()];
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, employees.data, openGroups]);
  const groupRows = useCompanyInvoicesGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<CompanyInvoiceRow>[] | undefined>(() => {
    if (!isGrouped || !employees.data?.length) return undefined;
    return employees.data.map((e) => {
      const key = invoiceOwnerGroupKey(e.owner_id);
      const idx = openList.findIndex((o) => o.key === key);
      const q = idx >= 0 ? groupRows[idx] : undefined;
      const name = formatFullName(e.first_name, e.last_name);
      return {
        key,
        label: name !== "—" ? name : e.email ?? "Unknown",
        avatarSrc: e.avatar_url,
        avatarInitials: getInitials(e.first_name, e.last_name, e.email),
        rows: q?.data?.items ?? [],
        total: e.count,
        loading: Boolean(q?.isFetching),
        error: Boolean(q?.isError),
        onRetry: () => void q?.refetch(),
      };
    });
  }, [isGrouped, employees.data, openList, groupRows]);
  const rows = isGrouped ? (groups ?? []).flatMap((g) => g.rows) : list.data?.items ?? [];
  const paginationMeta = companyInvoicesPagination(list.data, params);
  const pagination: PaginationState | undefined = !isGrouped && list.data
    ? {
        page: paginationMeta.page,
        limit: paginationMeta.limit,
        total: paginationMeta.total,
        totalPages: paginationMeta.totalPages,
      }
    : undefined;

  const setParams = (next: CompanyInvoicesListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  // (24-08-2026) Both cards were wrong in the same two ways: the amount came
  // from `totalAmountCents`, a currency-BLIND sum, rendered with a hard-coded
  // "GBP"; and the open figure was summed from the rows of the CURRENT PAGE, so
  // it silently stopped growing past `pageSize`. The backend now returns a
  // per-currency split and a server-wide open total.
  const totals = useMemo(() => resolveInvoiceTotals(list.data), [list.data]);
  const mixedCurrency = isMixedCurrency(totals);

  const downloadPdf = async (id: string) => {
    setDownloadingId(id);
    try {
      const res = await apiClient.get(`${invoiceEndpoints.byId(id)}/pdf`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Download failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const exportZip = async () => {
    setExporting(true);
    try {
      const url = companyInvoicesExportUrl({
        from: params.from,
        to: params.to,
        employee_id: params.employee_id,
        period: params.period as InvoicePeriod | undefined,
      });
      const res = await apiClient.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "invoices_export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Export failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<CompanyInvoiceRow>[]>(
    () => [
      {
        id: "invoice",
        // Over the indented invoice ids when grouped (DES 01-10-2026).
        header: () => <span className={isGrouped ? "pl-7" : undefined}>Invoice</span>,
        cell: ({ row }) => {
          const inv = row.original;
          if (isGrouped) {
            return (
              <p className="truncate pl-7 font-mono text-xs text-foreground">
                {inv.id}
              </p>
            );
          }
          const label = invoiceEmployeeLabel(inv);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={inv.ownerAvatarUrl}
                initials={getInitials(
                  inv.ownerName?.split(" ")[0],
                  inv.ownerName?.split(" ").slice(1).join(" "),
                  inv.ownerEmail,
                )}
                alt=""
                size="md"
                tone="brand"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{label}</p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {inv.id}
                </p>
              </div>
            </div>
          );
        },
      },
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => {
          const at = invoiceCreatedAt(row.original);
          return (
            <span className="text-xs text-muted-foreground">
              {at ? formatDateTime(at) : "—"}
            </span>
          );
        },
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => (
          // Collected vs billed, refunds (18-10-2026, shared/billing/moneyStatus).
          <span className="text-sm font-medium">
            <MoneyAmount
              row={row.original as MoneyRowLike}
              currency={row.original.currency ?? "GBP"}
            />
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        // Paid / part paid / failed / refunded, plus Deleted and Disputed
        // markers (18-10-2026): the same reading on every money screen.
        cell: ({ row }) => <MoneyBadges row={row.original as MoneyRowLike} />,
      },
      {
        id: "billing",
        header: "Billing",
        cell: ({ row }) =>
          row.original.billedToCompany ? (
            <Badge variant="success">Corporate</Badge>
          ) : (
            <Badge variant="outline">Personal</Badge>
          ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const inv = row.original;
          const busy = downloadingId === inv.id;
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <InvoiceMenu
                invoiceId={inv.id}
                status={inv.status}
                align="end"
                open={previewId === inv.id}
                onOpenChange={(open) => setPreviewId(open ? inv.id : null)}
              >
                <Button type="button" variant="ghost" size="icon" title="View invoice">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </InvoiceMenu>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Download PDF"
                disabled={busy}
                onClick={() => void downloadPdf(inv.id)}
              >
                {busy ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          );
        },
      },
    ],
    [downloadingId, isGrouped, previewId],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Invoices</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Company payment history. Filter by employee, period, or search.
              Preview HTML or download PDFs; export a ZIP for your accountant.
            </p>
          </div>
          {/* Filters top right, as on Sessions (DES 01-10-2026). */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <CompanyInvoicesHeaderFilters value={params} onChange={setParams} />
          </div>
        </GridBand>

        <GridCells className="grid-cols-1 sm:grid-cols-2">
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Total spend (filtered)"
              icon={PoundSterling}
              tone="brand"
              value={formatMoneyMinor(totals.amountCents, totals.currency)}
              hint={
                mixedCurrency
                  ? `Plus ${totals.others
                      .map((c) => formatMoneyMinor(c.amountCents, c.currency))
                      .join(", ")} in other currencies`
                  : "Sum of invoice amounts matching current filters"
              }
              isLoading={list.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Open (filtered)"
              icon={Receipt}
              tone="warning"
              value={formatMoneyMinor(totals.openAmountCents, totals.currency)}
              hint="Uncaptured across every matching row, not just this page"
              isLoading={list.isLoading}
            />
          </GridCell>
        </GridCells>

        <GridCell padded={false} className="border-b-0">
          <DataTable<CompanyInvoiceRow, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            groupItemLabel="invoices"
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            isLoading={isGrouped ? employees.isLoading : list.isLoading}
            isFetching={isGrouped ? employees.isFetching : list.isFetching}
            error={isGrouped ? employees.error : list.error}
            onRetry={() => (isGrouped ? employees.refetch() : list.refetch())}
            rowKey={(i) => i.id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(pageSize) =>
              setParams({ ...params, pageSize, page: 1 })
            }
            onRowClick={(inv) => setPreviewId(inv.id)}
            emptyShape="invoice"
            emptyTitle="No invoices in this period"
            emptyDescription="Invoices appear when sessions complete with a billable charge. Try All time or check Sessions for recent activity."
          />
        </GridCell>
      </GridBlock>

      {/* The Notes screen's search (DES 01-10-2026), docked at the bottom
          like Notes: "dock style ki tarha neechay hoon". */}
      <PageDock>
        <CompanyInvoicesFilters value={params} onChange={setParams} />
        {/* Export beside the search (DES 01-10-2026: "export zip ko search
            kay saath move karo"), in the same glass. */}
        <button
          type="button"
          disabled={exporting || list.isLoading}
          onClick={() => void exportZip()}
          className={cn(
            searchGlass,
            "flex h-14 items-center gap-2 px-6 text-[15px] font-semibold text-foreground transition-colors hover:bg-background disabled:opacity-60",
          )}
        >
          {exporting ? (
            <LoaderCircle className="h-5 w-5 animate-spin" />
          ) : (
            <FileArchive className="h-5 w-5 text-brand" strokeWidth={1.75} />
          )}
          Export ZIP
        </button>
      </PageDock>
    </div>
  );
}
